import {
  Col,
  Flex,
  Form,
  Grid,
  Input,
  message,
  Row,
  Typography,
  Switch,
  Card,
  Divider,
  Checkbox,
} from 'antd';
import { stickyTop } from '@/components/layout/MobileHeader/stickyTop';
import React, { FC, Fragment, useEffect, useState } from 'react';
import { FloatingLabel } from 'src/components/ui/FloatingLabel';
import { Hint } from '../assets/Hint';
import { CustomButton } from 'src/components/ui';
import { Header } from '../assets/Header';
import { updateBillingContacts, updateProfile } from '@/api/user';
import { useMutation } from '@tanstack/react-query';
import { BackButton } from '@/components/shared';
import { ROUTES } from '@/types/routes';
import { useNavigate } from 'react-router';
import queryClient from '@/client/queryClient';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectProfileCompleteness, selectUser } from '@/slice/user';
import Blocker from '@/components/blocker';
import { ProfileProgressBar } from '@/components/shared/profile-progress-bar';
import './index.scss';
import { GeneralUser, User, UserRole } from '@/types';
import { ChangeEmailModal } from './ChangeEmailModal';

export const Contact: FC = () => {
  const [form] = Form.useForm();

  const navigate = useNavigate();
  const [agreeSms, setAgreeSMS] = useState<boolean>(false);

  const toggleAgreeSMS = () => {
    setAgreeSMS((prev) => !prev);
  };

  const user = useAppSelector(selectUser);
  const profileCompleteness = useAppSelector(selectProfileCompleteness);

  const [formChanged, setFormChanged] = useState(false);

  const [franchisors, setFranchisors] = useState<User[]>([]);

  // Local, optimistic copy of the franchise's billing contacts. Seeded from
  // the server profile and updated immediately on toggle, so rapid toggles
  // compute the next list from the latest local set instead of stale Redux —
  // preventing a lost update (e.g. [A,B] then stale [A,C] silently dropping B).
  const [billingContacts, setBillingContacts] = useState<string[]>([]);

  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const { lg } = Grid.useBreakpoint();

  useEffect(() => {
    // Initialize franchisors array from user data
    if (user?.franchisors) {
      setFranchisors(user.franchisors);
    }

    // Find current user in franchisors and set form values
    const currentUser =
      user?.franchisors?.find((franchisor) => franchisor.id === user.userId) ||
      user;
    form.setFieldsValue({ ...currentUser, ...user });

    // Re-seed the optimistic billing-contact set from server truth on every
    // profile load/refetch (falls back to the legacy single billingAddress).
    setBillingContacts(
      user?.billingContacts?.length
        ? user.billingContacts
        : user?.billingAddress
          ? [user.billingAddress]
          : [],
    );
  }, [form, user]);

  const { mutateAsync: billingContactsMutation, isPending: isBillingPending } =
    useMutation({
      mutationFn: updateBillingContacts,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['profile'],
        });
        message.success('Billing contacts updated successfully');
      },
      onError: (error: any) => {
        message.error(
          error?.message ||
            'Failed to update billing contacts. Please try again.',
        );
      },
    });

  const { mutateAsync: profileMutation } = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['profile'],
      });
      message.success('Profile updated successfully');
    },
    onError: (error: any) => {
      message.error(
        error.response.data.message ||
          'Failed to update profile. Please try again.',
      );
    },
  });

  // Email matching is case-insensitive: the backend stores contacts lowercased
  // while account emails may carry mixed case.
  const isBillingContact = (email?: string): boolean => {
    if (!email) return false;
    const target = email.trim().toLowerCase();
    return billingContacts.some(
      (contact) => contact.trim().toLowerCase() === target,
    );
  };

  const handleBillingToggle = async (
    email: string | undefined,
    checked: boolean,
  ) => {
    if (!email) {
      return;
    }

    const target = email.trim().toLowerCase();
    const without = billingContacts.filter(
      (contact) => contact.trim().toLowerCase() !== target,
    );

    // At least one billing contact must always remain.
    if (!checked && without.length === 0) {
      message.warning('At least one billing contact is required');
      return;
    }

    const next = checked ? [...without, email] : without;
    const previous = billingContacts;
    // Apply optimistically and persist; revert if the request fails. Switches
    // are disabled while pending (below), so updates can't overlap.
    setBillingContacts(next);
    try {
      await billingContactsMutation(next);
    } catch {
      setBillingContacts(previous);
    }
  };

  const onFinish = (values: Partial<GeneralUser>) => {
    const data: Partial<GeneralUser> = {};
    const currentUser =
      franchisors.find((franchisor) => franchisor.id === user.id) || user;

    Object.keys(values).forEach((key) => {
      const typedKey = key as keyof GeneralUser;
      const value = values[typedKey];
      if (value !== undefined) {
        (data as any)[typedKey] = value;
      }
    });

    profileMutation({
      data,
    });

    setFormChanged(false);
  };

  const getCurrentUser = () => {
    return (
      franchisors.find((franchisor) => franchisor.id === user.userId) || user
    );
  };

  const getOtherUsers = () => {
    return franchisors.filter((franchisor) => franchisor.id !== user.userId);
  };

  return (
    <Fragment>
      <Blocker formChanged={formChanged} onFinish={onFinish} form={form} />
      <Row className={'my-profile-contact'}>
        {!lg && (
          <Col span={24}>
            <BackButton />
          </Col>
        )}

        <Col span={24} lg={{ span: 12 }} className={'header'}>
          <Header
            title={'Contact Info'}
            subtitle={
              'Update your contact information to allow us to reach you.'
            }
          />
        </Col>

        <Col
          span={24}
          lg={{ span: 12 }}
          style={{
            position: 'sticky',
            top: stickyTop(24),
            zIndex: 998,
          }}
        >
          <ProfileProgressBar align={'top'} steps={profileCompleteness} />
        </Col>

        <Col span={24} lg={{ span: 12 }}>
          <Form
            form={form}
            onFinish={onFinish}
            layout={'vertical'}
            requiredMark={false}
            onValuesChange={() => {
              setFormChanged(true);
            }}
            className={'change-password-form'}
          >
            {/* Company Information */}
            <Typography.Title level={4} style={{ marginBottom: 16 }}>
              Company Information
            </Typography.Title>

            <FloatingLabel label={'Work Phone Number'} childrenType="input">
              <Form.Item label={'Work Phone Number'} name={'workPhone'}>
                <Input size={'large'} />
              </Form.Item>
            </FloatingLabel>

            <FloatingLabel label={'Corporate Address'} childrenType="input">
              <Form.Item label={'Corporate Address'} name={'corporateAddress'}>
                <Input size={'large'} />
              </Form.Item>
            </FloatingLabel>

            <FloatingLabel label={'Corporate Website'} childrenType="input">
              <Form.Item label={'Corporate Website'} name={'corporateWebsite'}>
                <Input size={'large'} />
              </Form.Item>
            </FloatingLabel>

            {/* <Flex vertical gap={8} style={{ marginTop: 'auto' }}>
              {!lg && (
                <CustomButton
                  type={'default'}
                  text={'CANCEL'}
                  onClick={() => navigate(ROUTES.MY_PROFILE)}
                />
              )}

              <Form.Item noStyle>
                <CustomButton
                  htmlType={'submit'}
                  type={'primary'}
                  text={'SAVE CHANGES'}
                />
              </Form.Item>
            </Flex> */}

            {/* Current User Form */}
            <Typography.Title level={4} style={{ marginBottom: 16 }}>
              My Information
            </Typography.Title>

            <FloatingLabel label={'First Name'} childrenType="input">
              <Form.Item
                rules={[
                  { required: true, message: 'Please input your first name!' },
                ]}
                label={'First Name'}
                name={'firstName'}
              >
                <Input size={'large'} />
              </Form.Item>
            </FloatingLabel>

            <FloatingLabel label={'Last Name'} childrenType="input">
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: 'Please input your last name!',
                  },
                ]}
                label={'Last Name'}
                name={'lastName'}
              >
                <Input size={'large'} />
              </Form.Item>
            </FloatingLabel>

            <FloatingLabel label={'Email'} childrenType="input">
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: 'Please input correct email!',
                    type: 'email',
                  },
                ]}
                label={'Email'}
                name={'email'}
              >
                <Input size={'large'} disabled={true} />
              </Form.Item>
            </FloatingLabel>

            <Flex
              justify={'flex-end'}
              style={{ marginTop: -4, marginBottom: 12 }}
            >
              <CustomButton
                type={'link'}
                text={'Change Email'}
                onClick={() => {
                  if (formChanged) {
                    message.warning(
                      'Please save or discard your changes before changing your email.',
                    );
                    return;
                  }
                  setEmailModalOpen(true);
                }}
                style={{ padding: 0, height: 'auto' }}
              />
            </Flex>

            <FloatingLabel label={'Phone Number'} childrenType="input">
              <Form.Item label={'Phone Number'} name={'phone'}>
                <Input size={'large'} />
              </Form.Item>
            </FloatingLabel>

            {user.role === UserRole.FRANCHISEE && (
              <Form.Item name={'smsConsent'} valuePropName="checked">
                <Checkbox checked={agreeSms} onChange={toggleAgreeSMS}>
                  <Flex gap={6}>
                    <Typography.Text
                      className={'agree-receive-sms'}
                      style={{ fontSize: 12 }}
                    >
                      I agree to receive SMS messages, including messages from
                      consultants looking to introduce candidates to my brand.
                    </Typography.Text>
                  </Flex>
                </Checkbox>
              </Form.Item>
            )}

            {/* Billing Account Toggle for Current User */}
            <div style={{ marginTop: 16, marginBottom: 40 }}>
              <Flex justify="space-between" align="center">
                <Typography.Text strong>Set as Billing Contact</Typography.Text>
                <Switch
                  checked={isBillingContact(getCurrentUser()?.email)}
                  disabled={isBillingPending}
                  onChange={(checked) =>
                    handleBillingToggle(getCurrentUser()?.email, checked)
                  }
                />
              </Flex>
            </div>

            <Flex vertical gap={8} style={{ marginTop: 'auto' }}>
              {!lg && (
                <CustomButton
                  type={'default'}
                  text={'CANCEL'}
                  onClick={() => navigate(ROUTES.MY_PROFILE)}
                />
              )}

              <Form.Item noStyle>
                <CustomButton
                  htmlType={'submit'}
                  disabled={!formChanged}
                  type={'primary'}
                  text={'SAVE CHANGES'}
                />
              </Form.Item>
            </Flex>

            {/* Other Users Display */}
            {getOtherUsers().length > 0 && (
              <>
                <Typography.Title
                  level={4}
                  style={{ marginBottom: 16, marginTop: 62 }}
                >
                  Other Contacts
                </Typography.Title>

                {getOtherUsers().map((franchisor) => (
                  <Card
                    key={franchisor.id}
                    size="small"
                    style={{ marginBottom: 16 }}
                    title={`${franchisor.firstName} ${franchisor.lastName}`}
                  >
                    <Row gutter={[16, 8]}>
                      <Col span={12}>
                        <Typography.Text type="secondary">
                          Email:
                        </Typography.Text>
                        <br />
                        <Typography.Text>{franchisor.email}</Typography.Text>
                      </Col>
                      <Col span={12}>
                        <Typography.Text type="secondary">
                          Phone:
                        </Typography.Text>
                        <br />
                        <Typography.Text>
                          {franchisor.phone || 'Not provided'}
                        </Typography.Text>
                      </Col>
                      <Col span={24}>
                        <Flex
                          justify="space-between"
                          align="center"
                          style={{ marginTop: 12 }}
                        >
                          <Typography.Text strong>
                            Billing Contact
                          </Typography.Text>
                          <Switch
                            checked={isBillingContact(franchisor.email)}
                            disabled={isBillingPending}
                            onChange={(checked) =>
                              handleBillingToggle(franchisor.email, checked)
                            }
                          />
                        </Flex>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </>
            )}
          </Form>
        </Col>

        {lg && (
          <Col span={12}>
            <Hint
              wrapperStyle={{
                marginLeft: 'auto',
              }}
              hint={`This section contains essential contact details for your franchise. You can only edit your own information, but you can manage billing contact settings for all franchisors. You can set multiple billing contacts, and every one of them will receive invoices. At least one billing contact is required.`}
            />
          </Col>
        )}
      </Row>

      {/* Billing-contact remapping on a confirmed email change is handled
          server-side in confirmEmailChange; the modal invalidates ['profile']
          on success, so the refreshed billingContacts flow back in here. */}
      <ChangeEmailModal
        open={emailModalOpen}
        currentEmail={getCurrentUser()?.email}
        onClose={() => setEmailModalOpen(false)}
      />
    </Fragment>
  );
};
